import * as React from 'react';
import { I18n } from 'react-redux-i18n';
import { FormControl } from '@common/components/form-control';
import cx from 'classnames';
import isNil from 'lodash/isNil';

import { Flex } from '@common/components/Flex';
import Tree, { getItemParents } from '@common/components/Tree';
import { getTestAttributes } from '@common/utils/tests';
import { FolderSettingUpdateDto } from '@common/dto/folder-settings';
import { FolderGetDto } from '@common/dto/folder/get-dto';
import { FolderListDto } from '@common/dto/folder/list-dto';
import { TreeGetIdArgs } from '@common/components/Tree/tree.types';

import './folders.styles.scss';

type FoldersProps = {
    selectedFolder?: FolderGetDto;
    folderName: FolderSettingUpdateDto['name'];
    onFolderNameChange: (name: FolderSettingUpdateDto['name']) => void;
    onSelectedFolderChange: (folder: FolderGetDto) => void;
    folders: FolderListDto[];
    showFolderName: boolean;
    disabled?: boolean;
    classes?: {
        folders?: string;
        foldersTree?: string;
    }
}

export const Folders = ({ selectedFolder, folders, folderName, onFolderNameChange, onSelectedFolderChange, showFolderName, disabled, classes }: FoldersProps) => {

    const [collapsedItems, setCollapsedItems] = React.useState<FolderListDto['id'][]>([]);

    const handleFolderNameChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        onFolderNameChange(e.target.value);
    }, [onFolderNameChange]);

    const handleSelectedFolderChange = React.useCallback((id: FolderListDto['id'], folder: FolderGetDto) => {
        onSelectedFolderChange(folder);
    }, [onSelectedFolderChange]);

    const handleOnCollapsedItemsChange = React.useCallback((folderIds: FolderListDto['id'][]) => {
       if(!isNil(selectedFolder?.id)) {
        const items = [selectedFolder!.id, ...getItemParents(selectedFolder!.id, folders).map(folder => folder.id)];
        setCollapsedItems(folderIds.filter(id => !items.includes(id)));
       }
    }, [setCollapsedItems, folders, selectedFolder?.id]);

    return (
        <div className={cx(classes?.foldersTree)}>
            {showFolderName && (
                <div className="FolderManagement__name">
                    <Flex>
                        <label
                            className="FolderManagement__name__label"
                            htmlFor={getTestAttributes({ id: 'tx-folder-settings-fld-name' }).id}
                            {...getTestAttributes({ id: 'lbl-folder-settings-fld-name' })}
                        >
                            {I18n.t('Folder Name')}
                        </label>
                        <FormControl
                            value={folderName}
                            {...getTestAttributes({ id: 'tx-folder-settings-fld-name' })}
                            onChange={handleFolderNameChange}
                        />
                    </Flex>
                </div>
            )}
            <div className={cx("FolderManagement__folders", classes?.foldersTree, {
                ["FolderManagement__folders__disabled"]: disabled
            })}>
                <Tree
                    getId={{
                        folder: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined 
                          ? `folder-settings-tree-folder-${ownIndex}` 
                          : `folder-settings-tree-folder-child-${data.id}`,
                        collapseIcon: ({ parentIndex, ownIndex, data }: TreeGetIdArgs) => parentIndex === undefined 
                          ? `folder-settings-tree-folder-${ownIndex}-exp-icon`
                          : `folder-settings-tree-folder-child-${ownIndex}-exp-icon`
                    }}
                    collapseAfterLevel={0}
                    items={folders}
                    value={selectedFolder?.id}
                    onChange={disabled ? undefined : handleSelectedFolderChange}
                    onCollapsedItemsChange={handleOnCollapsedItemsChange}
                    collapsedItems={collapsedItems}
                    features={disabled ? {
                        collapsing: false,
                    } : undefined}
                />
            </div>
        </div>
    )
}
